export default {
  namespaced: true,

  state: {
    balance: 0,
    usedPoints: 0,
    history: [],
    purchaseHistory: [],
    loading: false,
    error: null,
    lastUpdated: null
  },

  getters: {
    balance: (state) => state.balance,
    usedPoints: (state) => state.usedPoints,
    totalEarned: (state) => state.balance + state.usedPoints,
    availableBalance: (state) => state.balance,
    history: (state) => state.history,
    purchaseHistory: (state) => state.purchaseHistory,
    loading: (state) => state.loading,
    error: (state) => state.error,
    lastUpdated: (state) => state.lastUpdated,
    isBalanceLoaded: (state) => state.lastUpdated !== null
  },

  mutations: {
    SET_BALANCE(state, data) {
      state.balance = data.balance;
      state.usedPoints = data.used_credit;
      state.lastUpdated = new Date();
    },

    SET_HISTORY(state, data) {
      state.history = data;
    },

    SET_PURCHASE_HISTORY(state, data) {
      state.purchaseHistory = data;
    },

    SET_LOADING(state, loading) {
      state.loading = loading;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    CLEAR_ERROR(state) {
      state.error = null;
    },

    // Called after successful purchase
    ADD_POINTS(state, amount) {
      state.balance += amount;
      state.lastUpdated = new Date();
    },

    // Called after booking with points
    DEDUCT_POINTS(state, amount) {
      state.balance -= amount;
      state.usedPoints += amount;
      state.lastUpdated = new Date();
    },

    RESET_POINTS(state) {
      state.balance = 0;
      state.usedPoints = 0;
      state.history = [];
      state.purchaseHistory = [];
      state.error = null;
      state.lastUpdated = null;
    }
  },

  actions: {
    /**
     * Fetch user's points balance
     * @param {number} token - Auth token
     */
    async fetchBalance({ commit }, token) {
      try {
        commit('SET_LOADING', true);
        const axios = require('axios');
        
        const response = await axios.get('/api/credit/balance', {
          headers: { Authorization: `Bearer ${token}` }
        });

        const { balance, used_credit } = response.data.data;
        commit('SET_BALANCE', {
          balance: balance || 0,
          used_credit: used_credit || 0
        });
        commit('CLEAR_ERROR');

        return {
          balance: balance || 0,
          usedPoints: used_credit || 0
        };
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to fetch points balance';
        commit('SET_ERROR', message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch transaction history
     * @param {object} params - Query parameters (limit, page, type, status)
     * @param {number} token - Auth token
     */
    async fetchHistory({ commit }, { token, limit = 20, page = 1, type = null, status = null }) {
      try {
        commit('SET_LOADING', true);
        const axios = require('axios');
        
        const params = { limit, page };
        if (type) params.type = type;
        if (status) params.status = status;

        const response = await axios.get('/api/credit/history', {
          params,
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = Array.isArray(response.data.data) ? response.data.data : [];
        commit('SET_HISTORY', data);
        commit('CLEAR_ERROR');

        return {
          data: data,
          pagination: response.data.meta || response.data.pagination || {}
        };
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to fetch history';
        commit('SET_ERROR', message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch purchase history
     * @param {object} params - Query parameters (limit, page, status)
     * @param {number} token - Auth token
     */
    async fetchPurchaseHistory({ commit }, { token, limit = 20, page = 1, status = null }) {
      try {
        commit('SET_LOADING', true);
        const axios = require('axios');
        
        const params = { limit, page, type: 'credit' };
        if (status) params.status = status;

        const response = await axios.get('/api/credit/history', {
          params,
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = Array.isArray(response.data.data) ? response.data.data : [];
        commit('SET_PURCHASE_HISTORY', data);
        commit('CLEAR_ERROR');

        return {
          data: data,
          pagination: response.data.meta || response.data.pagination || {}
        };
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to fetch purchase history';
        commit('SET_ERROR', message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Initiate a points purchase
     * @param {object} params - { amount, token }
     */
    async initiatePurchase({ commit }, { amount, token }) {
      try {
        commit('SET_LOADING', true);
        const axios = require('axios');
        
        const response = await axios.post(
          '/api/credit/top-up',
          {
            amount: amount,
            description: 'Points purchase'
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        commit('CLEAR_ERROR');

        return {
          transactionId: response.data.data.transaction_id,
          invoiceUrl: response.data.data.invoice_url,
          paymentLink: response.data.data.payment_link,
          amount: response.data.data.amount
        };
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to initiate purchase';
        commit('SET_ERROR', message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Get purchase transaction details
     * @param {object} params - { transactionId, token }
     */
    async fetchPurchaseDetail({ commit }, { transactionId, token }) {
      try {
        commit('SET_LOADING', true);
        const axios = require('axios');
        
        const response = await axios.get(`/api/credit/top-up/${transactionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        commit('CLEAR_ERROR');
        return response.data.data;
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to fetch transaction';
        commit('SET_ERROR', message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Handle successful purchase
     * Optimistic update before API confirmation
     */
    optimisticAddPoints({ commit }, amount) {
      commit('ADD_POINTS', amount);
    },

    /**
     * Handle points deduction after booking
     */
    optimisticDeductPoints({ commit }, amount) {
      commit('DEDUCT_POINTS', amount);
    },

    /**
     * Clear all points data (on logout)
     */
    resetPointsData({ commit }) {
      commit('RESET_POINTS');
    },

    /**
     * Clear error message
     */
    clearError({ commit }) {
      commit('CLEAR_ERROR');
    }
  }
}
